<script setup>
/**
 * Cookie consent banner + preferences dialog.
 *
 * - Mounted once globally (App.vue) so it appears on backoffice, customer
 *   portal and (Android) the technician mobile app. Native iOS skips the
 *   prompt — see cookieConsent store (App Store 5.1.2(i)).
 * - Mobile-first: compact bottom sheet that never covers the whole screen.
 * - Strictly-necessary cookies are always on and locked.
 */
import { ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useCookieConsentStore } from '@/stores/cookieConsent'

const consent = useCookieConsentStore()
const { mobile } = useDisplay()

// Local editable copy for the preferences dialog
const draft = ref({ functional: false, analytics: false, marketing: false })

watch(
  () => consent.showSettings,
  (open) => {
    if (open) {
      const c = consent.categories
      draft.value = {
        functional: c.functional,
        analytics: c.analytics,
        marketing: c.marketing,
      }
    }
  }
)

const categoryDefs = [
  { key: 'necessary', icon: 'mdi-shield-lock', locked: true },
  { key: 'functional', icon: 'mdi-tune', locked: false },
  { key: 'analytics', icon: 'mdi-chart-line', locked: false },
  { key: 'marketing', icon: 'mdi-bullhorn', locked: false },
]

function acceptAll() {
  consent.acceptAll()
}
function rejectAll() {
  consent.rejectAll()
}
function customize() {
  consent.openSettings()
}
function savePreferences() {
  consent.saveCustom(draft.value)
}
</script>

<template>
  <template v-if="consent.promptsEnabled">
  <!-- Consent banner (only until a choice is made) -->
  <Transition name="fsj-cookie-fade">
    <div
      v-if="consent.needsBanner && !consent.showSettings"
      class="fsj-cookie-banner"
      :class="{ 'fsj-cookie-banner--mobile': mobile }"
      role="dialog"
      aria-modal="false"
      :aria-label="$t('cookie.banner.title')"
    >
      <v-card elevation="8" rounded="lg" class="pa-4">
        <div class="d-flex align-start ga-3 mb-2">
          <v-icon color="primary" size="24">mdi-cookie</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold text-primary">
              {{ $t('cookie.banner.title') }}
            </div>
            <div class="text-body-2 text-medium-emphasis mt-1">
              {{ $t('cookie.banner.description') }}
            </div>
            <router-link to="/cookies" class="text-decoration-none">
              <span class="text-body-2 fsj-cookie-link">{{ $t('cookie.banner.policyLink') }}</span>
            </router-link>
          </div>
        </div>

        <div
          class="d-flex ga-2 mt-3"
          :class="mobile ? 'flex-column' : 'flex-row justify-end'"
        >
          <v-btn
            color="accent"
            variant="flat"
            :block="mobile"
            @click="acceptAll"
          >
            {{ $t('cookie.actions.acceptAll') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="outlined"
            :block="mobile"
            @click="rejectAll"
          >
            {{ $t('cookie.actions.rejectAll') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="outlined"
            :block="mobile"
            @click="customize"
          >
            {{ $t('cookie.actions.customize') }}
          </v-btn>
        </div>
      </v-card>
    </div>
  </Transition>

  <!-- Preferences dialog (Cookie settings) -->
  <v-dialog
    v-model="consent.showSettings"
    :fullscreen="mobile"
    max-width="520"
    scrollable
  >
    <v-card rounded="lg">
      <v-card-title class="d-flex align-center ga-2 pa-4">
        <v-icon color="primary" size="22">mdi-cookie-cog</v-icon>
        <span class="text-h6 text-primary">{{ $t('cookie.settings.title') }}</span>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" size="small" @click="consent.closeSettings()" />
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-0">
        <div class="text-body-2 text-medium-emphasis pa-4 pb-2">
          {{ $t('cookie.settings.intro') }}
        </div>

        <template v-for="(cat, idx) in categoryDefs" :key="cat.key">
          <div class="d-flex align-center ga-3 pa-4 py-3">
            <v-icon :color="cat.locked ? 'medium-emphasis' : 'primary'" size="22">{{ cat.icon }}</v-icon>
            <div class="flex-grow-1">
              <div class="text-subtitle-2 font-weight-medium">
                {{ $t(`cookie.categories.${cat.key}.title`) }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ $t(`cookie.categories.${cat.key}.description`) }}
              </div>
            </div>
            <v-switch
              v-if="cat.locked"
              :model-value="true"
              color="accent"
              density="compact"
              hide-details
              disabled
              inset
            >
              <template #append>
                <v-icon size="16" color="medium-emphasis">mdi-lock</v-icon>
              </template>
            </v-switch>
            <v-switch
              v-else
              v-model="draft[cat.key]"
              color="accent"
              density="compact"
              hide-details
              inset
            />
          </div>
          <v-divider v-if="idx < categoryDefs.length - 1" />
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4 d-flex ga-2" :class="mobile ? 'flex-column' : ''">
        <v-btn
          color="accent"
          variant="flat"
          :block="mobile"
          @click="savePreferences"
        >
          {{ $t('cookie.actions.savePreferences') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="outlined"
          :block="mobile"
          @click="rejectAll"
        >
          {{ $t('cookie.actions.rejectAll') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </template>
</template>

<style scoped>
.fsj-cookie-banner {
  position: fixed;
  left: 16px;
  bottom: 16px;
  width: 420px;
  max-width: calc(100vw - 32px);
  z-index: 2000;
}
.fsj-cookie-banner--mobile {
  left: 8px;
  right: 8px;
  bottom: 8px;
  width: auto;
  max-width: none;
}
.fsj-cookie-link {
  color: rgb(var(--v-theme-accent));
  text-decoration: underline;
  cursor: pointer;
}
.fsj-cookie-fade-enter-active,
.fsj-cookie-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fsj-cookie-fade-enter-from,
.fsj-cookie-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
